import { apiClient } from '../client';

/**
 * Endpoints relacionados a notas e boletins
 */

/**
 * Cria ou atualiza uma nota para um aluno
 * @param {Object} gradeData - Dados da nota
 * @param {number} gradeData.AlunoID - ID do aluno
 * @param {number} gradeData.TurmaID - ID da turma
 * @param {number} gradeData.DisciplinaID - ID da disciplina
 * @param {number} gradeData.period - Período (1-4)
 * @param {string} [gradeData.ac1] - Nota AC1
 * @param {string} [gradeData.ac2] - Nota AC2
 * @param {string} [gradeData.mb] - Média bimestral
 * @param {string} [gradeData.rm] - Recuperação mensal
 * @param {boolean} [gradeData.visibility] - Visibilidade da nota
 * @returns {Promise<Object>} Resultado da operação
 */
const createOrUpdateGrade = async (gradeData) => {
  const { data } = await apiClient.post('/grades/new', gradeData);
  return data;
};

/**
 * Atualiza todas as notas de uma turma, disciplina e período
 * @param {Object} updateData - Dados da atualização
 * @param {number} updateData.TurmaID - ID da turma
 * @param {number} updateData.DisciplinaID - ID da disciplina
 * @param {number} updateData.period - Período (1-4)
 * @param {Object} updateData.values - Valores das notas a atualizar
 * @returns {Promise<Object>} Resultado da operação
 */
const updateAllGrades = async (updateData) => {
  const { data } = await apiClient.post('/grades/update/all', updateData);
  return data;
};

/**
 * Lista todas as notas cadastradas
 * @returns {Promise<Array>} Lista de notas
 */
const getAllGrades = async () => {
  const { data } = await apiClient.get('/grades/list');
  return data.grades || [];
};

/**
 * Lista notas por turma, disciplina e/ou período
 * @param {Object} filters - Filtros de busca
 * @param {number} filters.TurmaID - ID da turma (obrigatório)
 * @param {number} [filters.DisciplinaID] - ID da disciplina (opcional)
 * @param {number} [filters.period] - Período (opcional)
 * @returns {Promise<Array>} Lista de notas filtradas
 */
const getGradesByClass = async (filters) => {
  if (!filters.TurmaID) {
    throw new Error('TurmaID é obrigatório');
  }
  
  // Normalizar os filtros para garantir tipos corretos
  const normalizedFilters = {
    TurmaID: typeof filters.TurmaID === 'string' ? parseInt(filters.TurmaID, 10) : Number(filters.TurmaID),
    DisciplinaID: filters.DisciplinaID 
      ? (typeof filters.DisciplinaID === 'string' ? parseInt(filters.DisciplinaID, 10) : Number(filters.DisciplinaID))
      : undefined,
    period: filters.period ? String(filters.period) : filters.period,
  };
  
  // Validar tipos
  if (isNaN(normalizedFilters.TurmaID) || normalizedFilters.TurmaID <= 0) {
    throw new Error('TurmaID deve ser um número positivo');
  }
  
  if (normalizedFilters.DisciplinaID && (isNaN(normalizedFilters.DisciplinaID) || normalizedFilters.DisciplinaID <= 0)) {
    throw new Error('DisciplinaID deve ser um número positivo');
  }
  
  try {
    const { data } = await apiClient.post('/grades/list/classroom', normalizedFilters);
    return data.grades || [];
  } catch (error) {
    console.error('[getGradesByClass] Erro ao buscar notas:', {
      error: error.message,
      url: apiClient.defaults.baseURL,
      endpoint: '/grades/list/classroom',
      filters
    });
    throw error;
  }
};

/**
 * Salva notas finais e situação de um aluno
 * @param {Object} finalData - Dados finais
 * @param {number} finalData.TurmaID - ID da turma
 * @param {number} finalData.AlunoID - ID do aluno
 * @param {Array} finalData.grades - Array de notas por disciplina
 * @returns {Promise<Object>} Resultado da operação
 */
const saveFinalGrades = async (finalData) => {
  // Normalizar os dados para garantir que disciplinaId seja número
  const normalizedData = {
    ...finalData,
    AlunoID: typeof finalData.AlunoID === 'string' ? parseInt(finalData.AlunoID, 10) : Number(finalData.AlunoID),
    TurmaID: typeof finalData.TurmaID === 'string' ? parseInt(finalData.TurmaID, 10) : Number(finalData.TurmaID),
    grades: finalData.grades?.map(grade => ({
      ...grade,
      disciplinaId: typeof grade.disciplinaId === 'string' 
        ? parseInt(grade.disciplinaId, 10) 
        : Number(grade.disciplinaId),
    })) || [],
  };
  
  const { data } = await apiClient.post('/grades/final/add', normalizedData);
  return data;
};

/**
 * Obtém o boletim de um aluno
 * @param {number} AlunoID - ID do aluno
 * @param {number} TurmaID - ID da turma
 * @returns {Promise<Object>} Boletim do aluno
 */
const getBoletimByStudent = async (AlunoID, TurmaID) => {
  const { data } = await apiClient.post('/grades/boletim', { AlunoID, TurmaID });
  return data;
};

/**
 * Obtém o boletim de um aluno formatado para o app
 * @param {number} AlunoID - ID do aluno
 * @param {number} TurmaID - ID da turma
 * @returns {Promise<Object>} Boletim formatado
 */
const getBoletimByStudentApp = async (AlunoID, TurmaID) => {
  const { data } = await apiClient.post('/grades/final/list/app', { AlunoID, TurmaID });
  return data;
};

/**
 * Define ou atualiza a legenda de notas para uma turma e período
 * @param {Object} captionData - Dados da legenda
 * @param {number} captionData.TurmaID - ID da turma
 * @param {number} captionData.period - Período (1-4)
 * @param {string} captionData.caption - Texto da legenda
 * @returns {Promise<Object>} Resultado da operação
 */
const setGradesCaption = async (captionData) => {
  const { data } = await apiClient.post('/grades/captions', captionData);
  return data;
};

/**
 * Lista as legendas de notas por turma e período
 * @param {number} TurmaID - ID da turma
 * @param {number} period - Período (1-4)
 * @returns {Promise<Object>} Legenda de notas
 */
const getGradesCaption = async (TurmaID, period) => {
  const { data } = await apiClient.post('/grades/captions/list', { TurmaID, period });
  return data;
};

export {
  createOrUpdateGrade,
  updateAllGrades,
  getAllGrades,
  getGradesByClass,
  saveFinalGrades,
  getBoletimByStudent,
  getBoletimByStudentApp,
  setGradesCaption,
  getGradesCaption,
};

